// ABOUTME: Supplies accessible local controls styled by NovaName semantic tokens.
// ABOUTME: Radix owns dialog focus, keyboard behavior, and tab selection.
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { LoaderCircle, X } from "./icons";

export function Button({
  variant = "primary",
  busy = false,
  className = "",
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger" | "inverse";
  busy?: boolean;
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || busy}
      className={`button button-${variant} ${className}`}
      aria-busy={busy || undefined}
    >
      {busy && <LoaderCircle className="spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  className = "",
  returnFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  returnFocus?: () => HTMLElement | null;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) return;
    // Controlled dialogs are opened by external controls rather than Dialog.Trigger.
    const rememberFocus = () => {
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active !== document.body &&
        !contentRef.current?.contains(active)
      ) {
        returnFocusRef.current = active;
      }
    };
    rememberFocus();
    document.addEventListener("focusin", rememberFocus);
    return () => document.removeEventListener("focusin", rememberFocus);
  }, [open]);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          ref={contentRef}
          className={`dialog-content ${className}`}
          {...(!description ? { "aria-describedby": undefined } : {})}
          onCloseAutoFocus={(event) => {
            const target = returnFocus?.() ?? returnFocusRef.current;
            if (target?.isConnected) {
              event.preventDefault();
              target.focus({ preventScroll: true });
            }
          }}
        >
          <div className="dialog-heading">
            <div>
              <Dialog.Title className="dialog-title">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="dialog-description">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <Button
                variant="quiet"
                className="icon-button"
                aria-label="Close dialog"
              >
                <X />
              </Button>
            </Dialog.Close>
          </div>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Tabs whose content renders elsewhere on the page; that content sits in a TabPanel with the same panelId.
export function TabNav({
  value,
  onValueChange,
  tabs,
  label,
  panelId,
}: {
  value: string;
  onValueChange: (value: string) => void;
  tabs: { id: string; label: ReactNode }[];
  label: string;
  panelId: string;
}) {
  return (
    <Tabs.Root value={value} onValueChange={onValueChange} className="tab-root">
      <Tabs.List className="tabs" aria-label={label}>
        {tabs.map((tab) => (
          <Tabs.Trigger
            className="tab"
            value={tab.id}
            key={tab.id}
            id={`${panelId}-tab-${tab.id}`}
            aria-controls={panelId}
          >
            {tab.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
    </Tabs.Root>
  );
}

// Without tabs on the page (withTabs false) the content stays in place without a panel role.
export function TabPanel({
  panelId,
  value,
  withTabs = true,
  className,
  children,
}: {
  panelId: string;
  value: string;
  withTabs?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      id={panelId}
      className={className}
      {...(withTabs
        ? { role: "tabpanel", "aria-labelledby": `${panelId}-tab-${value}` }
        : {})}
    >
      {children}
    </div>
  );
}

export function Notice({
  children,
  kind = "info",
  action,
}: {
  children: ReactNode;
  kind?: "info" | "error" | "success";
  action?: ReactNode;
}) {
  return (
    <div
      className={`notice notice-${kind}`}
      role={kind === "error" ? "alert" : "status"}
    >
      <div>{children}</div>
      {action}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
  className = "",
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`field ${className}`}>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
